<template>
  <div class="mt-4 px-3">
    <div class="row">
      <div class="table-responsive">
        <table class="table table-striped table-bordered">
          <thead class="cusmtom-thead-color">
            <tr class="cusmtom-thead-color">
              <th class="text-center p-3 custom-header-label" rowspan="2">
                {{ $t('sigatoka.plant') }}
              </th>
              <th class="text-center p-3 custom-header-label" rowspan="2">
                {{ $t('sigatoka.total_functional_leaves') }}
              </th>
              <th class="text-center p-3 custom-header-label" rowspan="2">
                {{ $t('sigatoka.ref') }}
              </th>
              <th
                class="text-center p-3 custom-header-label"
                rowspan="2"
                v-for="(leaf, index) in leaves"
                :key="index"
              >
                {{ $t('sigatoka.leaf') + ' ' + leaf }}
              </th>
              <th class="text-center p-3 custom-header-label" rowspan="2">
                {{ $t('common_words.options') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(plant, index) in plants"
              :key="plant.id || `new-${index}`"
              v-if="!plant.deleted"
            >
              <td class="col-12 col-md-1 col-lg-1 col-xl-1 p-0">
                <fg-input
                  class="m-3"
                  type="number"
                  v-model="plant.number"
                  @keypress="validateInput"
                ></fg-input>
              </td>
              <td class="col-12 col-md-2 col-lg-2 col-xl-2 p-0">
                <fg-input
                  class="m-3"
                  type="number"
                  v-model="plant.total_leaves"
                  @input="limitInput(plant, 'total_leaves')"
                  @keypress="validateInput"
                ></fg-input>
              </td>
              <td class="col-12 col-md-2 col-lg-2 col-xl-2 p-0">
                <fg-input
                  class="m-3"
                  type="number"
                  v-model="plant.ref"
                  @input="limitInput(plant, 'ref')"
                  @keypress="validateInput"
                ></fg-input>
              </td>
              <td
                v-for="(leaf, index) in leaves"
                :key="index + '-' + leaf"
                class="p-0"
              >
                <fg-input
                  class="m-3"
                  type="number"
                  v-model="plant.leaf_values[leaf]"
                  @input="limitLeafInput(plant, leaf)"
                  @keypress="validateInput"
                ></fg-input>
              </td>
              <td
                class="text-center custom-td-pd col-12 col-md-2 col-lg-2 col-xl-2 p-0"
                style="min-width: 120px"
              >
                <div class="d-flex justify-content-center m-3">
                  <a
                    v-if="isUpdate()"
                    v-tooltip.top-center="$t('common_words.add')"
                    class="btn-warning btn-simple btn-link custom-margin-actions"
                    @click.prevent="addRow()"
                  >
                    <em class="fa fa-plus custom-size-fa-actions"></em>
                  </a>
                  <a
                    v-if="isDelete() && activePlantsCount > 0"
                    v-tooltip.top-center="$t('common_words.erase')"
                    class="btn-danger btn-simple btn-link custom-margin-actions"
                    @click="removeRow(index)"
                  >
                    <em class="fa fa-times custom-size-fa-actions"></em>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import MixinMenu from 'src/util/mixinMenu';
import MixinMasters from 'src/util/mixinMasters';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinPermissions from 'src/util/mixinPermissions';

export default {
  name: 'young-table',
  mixins: [MixinMasters, MixinPermissions, MixinForm, MixinMenu],
  props: {
    leaves: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      plants: [],
    };
  },
  computed: {
    activePlantsCount() {
      return this.plants.filter((plant) => !plant.deleted).length;
    },
  },
  methods: {
    createLeafValues() {
      return this.leaves.reduce((acc, leaf) => {
        acc[leaf] = null;
        return acc;
      }, {});
    },
    createPlant(number) {
      return {
        id: null,
        total_leaves: null,
        ref: null,
        number,
        leaf_values: this.createLeafValues(),
        deleted: false,
      };
    },
    addRow() {
      const activePlants = this.plants.filter((plant) => !plant.deleted);
      const lastNumber =
        activePlants.length > 0
          ? Math.max(...activePlants.map((item) => Number(item.number) || 0))
          : 0;
      this.plants.push(this.createPlant(lastNumber + 1));
    },
    removeRow(index) {
      const plant = this.plants[index];
      if (plant.id) this.$set(plant, 'deleted', true);
      else this.plants.splice(index, 1);
      const activePlants = this.plants.filter((item) => !item.deleted);
      if (activePlants.length === 0) this.plants.push(this.createPlant(1));
    },
    getData() {
      return this.plants;
    },
    setData(plants) {
      if (!plants || !plants.length) {
        this.resetData();
        return;
      }
      this.plants = plants.map((plant) => {
        const leafValues = this.createLeafValues();
        if (plant.sigatoka_young_plant_leaves) {
          plant.sigatoka_young_plant_leaves.forEach((leaf) => {
            leafValues[leaf.leaf] = leaf.infection;
          });
        }

        return {
          id: plant.id,
          number: plant.plant,
          total_leaves: plant.functional_leaf,
          ref: plant.ref,
          leaf_values: leafValues,
          deleted: false,
        };
      });
    },
    resetData() {
      this.plants = [this.createPlant(1)];
    },
    validateInput(event) {
      const input = String.fromCharCode(event.which);
      if (!/[0-9,.]/.test(input)) event.preventDefault();
    },
    limitInput(object, field, max = 30) {
      const value = Number(object[field]);
      if (value > max) object[field] = max;
    },
    limitLeafInput(plant, leaf) {
      const value = Number(plant.leaf_values[leaf]);
      if (value > 10) plant.leaf_values[leaf] = 10;
    },
  },
  created() {
    this.plants.push(this.createPlant(1));
  },
};
</script>
<style scoped>
.card .table:not(.not-last-td) thead th:last-child {
  display: revert !important;
}

.custom-td-pd {
  padding: 0px !important;
}

.table-responsive {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 80vh;
}

.cusmtom-thead-color {
  position: sticky;
  top: 0;
  z-index: 2;
}
</style>
